import React from 'react';

// Shared form (Add + Edit) for plant master fields.
// `mode` controls the colour scheme: 'add' = indigo/green, 'edit' = orange.
const PlantForm = ({ mode = 'add', plant, setPlant, onSubmit, onCancel, loading }) => {
  const isEdit = mode === 'edit';
  const ring = isEdit ? 'focus:ring-orange-500' : 'focus:ring-indigo-500';
  const card = isEdit ? 'border-orange-200' : 'border-indigo-200';
  const submitBg = isEdit ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700';
  const submitLabel = loading ? (isEdit ? 'Updating...' : 'Adding...') : (isEdit ? 'Update Plant' : 'Add Plant');
  const banner = isEdit
    ? { bg: 'bg-orange-50 border-orange-200', text: 'text-orange-800',
        msg: 'To change rates (including CO2 purchase rate), use the "Manage Rates" tab.' }
    : { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-800',
        msg: 'Default rates will be applied. You can customize rates (including CO2 purchase rate) later from the "Manage Rates" tab.' };

  const capacityTestId = isEdit ? 'edit-plant-capacity-input' : 'new-plant-capacity-input';

  return (
    <div className={`bg-white rounded-lg shadow-md p-4 sm:p-6 border-2 ${card}`}>
      <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">{isEdit ? 'Edit Plant Details' : 'Add New Plant'}</h3>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Plant Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text" value={plant.name}
              onChange={(e) => setPlant({ ...plant, name: e.target.value })}
              className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 ${ring}`}
              placeholder="e.g., Plant C - Bangalore"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
            <input
              type="text" value={plant.location}
              onChange={(e) => setPlant({ ...plant, location: e.target.value })}
              className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 ${ring}`}
              placeholder="e.g., Bangalore"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Annual Capacity (MT/Year)</label>
            <input
              type="number" min="0" step="1"
              value={plant.annual_capacity_mt ?? 0}
              onChange={(e) => setPlant({ ...plant, annual_capacity_mt: parseFloat(e.target.value) || 0 })}
              className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 ${ring}`}
              placeholder="e.g., 2400"
              data-testid={capacityTestId}
            />
            {!isEdit && (
              <p className="text-xs text-gray-500 mt-1">Used in the Utilization dashboard. Leave 0 if unknown.</p>
            )}
          </div>
        </div>

        <div className={`border rounded-lg p-4 ${banner.bg}`}>
          <p className={`text-sm ${banner.text}`}>
            <strong>Note:</strong> {banner.msg}
          </p>
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:space-x-3 sm:gap-0">
          <button
            type="button" onClick={onCancel}
            className="px-6 py-2.5 min-h-[44px] border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit" disabled={loading}
            className={`${submitBg} text-white font-semibold py-2.5 px-6 min-h-[44px] rounded-lg transition-colors disabled:opacity-50`}
          >
            {submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PlantForm;
